import { Injectable } from '@angular/core';
import axios, { type AxiosResponse } from 'axios';
import { catchError, from, map, Observable, throwError } from 'rxjs';
import type { OryApiResponse } from '../model';
import { OryFlowValidationError, OryNetworkError, OryBrowserRedirectError, OryServiceError } from '../model';
import { transformOryError, validateSuccessResponse } from './error-parser';

/**
 * Transforms Axios responses/errors into RxJS observables with domain-specific error types.
 * 
 * Delegates error transformation to error-parser module for cleaner separation of concerns.
 */
@Injectable({
  providedIn: 'root'
})
export class OryRequestTransformer {

  fromOryRequest<ResponseType extends OryApiResponse>(
    request: Promise<AxiosResponse<ResponseType>>
  ): Observable<ResponseType> {
    return from(request).pipe(
      map(response => {
        // Validate successful responses for hidden errors (HTTP 200 with validation errors)
        // Only for POST/PUT/PATCH (submissions), not GET (retrieval)
        const method = response.config.method || 'GET';
        validateSuccessResponse(response.data, response.status, method);
        return response.data;
      }),
      catchError((error: unknown) => {
        // If it's already one of our custom error types (e.g., thrown by validateSuccessResponse),
        // preserve it as-is without wrapping
        if (
          error instanceof OryFlowValidationError ||
          error instanceof OryNetworkError ||
          error instanceof OryBrowserRedirectError ||
          error instanceof OryServiceError
        ) {
          return throwError(() => error);
        }

        // Type guard: ensure it's an Axios error
        if (!axios.isAxiosError(error)) {
          return throwError(() => new Error(
            error instanceof Error ? error.message : 'Unknown error occurred'
          ));
        }

        return throwError(() => transformOryError(error));
      })
    );
  }
}
